import { useEffect, useState, useRef, useMemo } from 'react';
import { tokens } from "../../theme";
import {
    useTheme, Box
} from '@mui/material';

import { createChart, CrosshairMode, PriceScaleMode } from 'lightweight-charts';

import { useFetchingData } from '../../components/api';

// ================= SKELETON COMPONENT (from RubberThai.jsx) =================
function ChartSkeleton({ title, height = 240 }) {
    return (
        <div className="bg-[#111827] border border-slate-700 rounded-xl flex flex-col overflow-hidden" style={{ height }}>
            <div className="px-5 py-4 border-b border-slate-700/50 flex items-center justify-between bg-[#0f172a] shrink-0" style={{ height: 60 }}>
                <p className="text-sm text-slate-300 font-bold uppercase tracking-wide">{title}</p>
                <div className="flex items-center gap-3">
                    <div className="h-5 w-14 rounded bg-slate-700 animate-pulse" />
                    <div className="h-5 w-24 rounded bg-slate-700 animate-pulse" />
                </div>
            </div>

            <div
                className="relative w-full flex-1 bg-[#0f172a] overflow-hidden"
                style={{ height: height - 60 }}
            >
                <svg className="absolute inset-0 w-full h-full" preserveAspectRatio="none">
                    {[...Array(5)].map((_, i) => {
                        const y = 15 + (i * ((height - 60) - 15 - 25)) / 4;
                        return (
                            <line
                                key={i}
                                x1="0"
                                y1={y}
                                x2="100%"
                                y2={y}
                                stroke="#1e293b"
                                strokeWidth="1"
                            />
                        );
                    })}
                </svg>

                <div className="absolute inset-0">
                    <div
                        className="absolute inset-0"
                        style={{
                            background:
                                "linear-gradient(90deg, transparent 0%, rgba(56,189,248,0.08) 40%, rgba(125,211,252,0.18) 50%, rgba(56,189,248,0.08) 60%, transparent 100%)",
                            animation: "shimmer 1.8s ease-in-out infinite",
                        }}
                    />
                </div>

                <style>{`
                    @keyframes shimmer {
                        0%   { transform: translateX(-100%); }
                        100% { transform: translateX(100%); }
                    }
                `}</style>
            </div>
        </div>
    );
}

// ================= HELPER FUNCTIONS =================
const compareDatetime = (a, b, field = 'DateTime') => {
    const datetimeA = new Date(a[field]);
    const datetimeB = new Date(b[field]);
    return datetimeA - datetimeB;
}

const filterByRange = (data, range) => {
    if (!Array.isArray(data)) return [];
    if (range === 'ALL' || range === 'MAX') return data;

    const currentDate = new Date();
    let fromDate;

    switch (range) {
        case '3M':
            fromDate = new Date(currentDate);
            fromDate.setMonth(currentDate.getMonth() - 3);
            break;
        case '6M':
            fromDate = new Date(currentDate);
            fromDate.setMonth(currentDate.getMonth() - 6);
            break;
        case '1Y':
            fromDate = new Date(currentDate);
            fromDate.setFullYear(currentDate.getFullYear() - 1);
            break;
        case 'YTD':
            fromDate = new Date(currentDate.getFullYear(), 0, 1);
            break;
        default:
            return data;
    }

    return data.filter(item => {
        const itemDate = new Date(item.DateTime || item.Date || item.date);
        return itemDate >= fromDate && itemDate <= currentDate;
    });
};

const generateData = (data, date_columm = 'DateTime', field) => {
    return data.map(d => {
        const dd = new Date(d[date_columm]);
        const year = dd.getFullYear();
        const month = String(dd.getMonth() + 1).padStart(2, '0');
        const day = String(dd.getDate()).padStart(2, '0');
        let time2 = `${year}-${month}-${day}`;
        return { time: time2, value: parseFloat(d[field]) || 0 };
    });
};

const generateUniqueRandomColors = (count) => {
    const colors = [];
    const usedColors = new Set();
    const getRandomColor = () => {
        const letters = '0123456789ABCDEF';
        let color = '#';
        for (let i = 0; i < 6; i++) {
            color += letters[Math.floor(Math.random() * 16)];
        }
        return color;
    };
    while (colors.length < count) {
        const color = getRandomColor();
        if (!usedColors.has(color)) {
            usedColors.add(color);
            colors.push(color);
        }
    }
    return colors;
};

const base_api_url =
    process.env.NODE_ENV === "production"
        ? window.location.protocol === "https:"
            ? process.env.REACT_APP_API_HTTPS_URL
            : process.env.REACT_APP_API_HTTP_URL
        : "/application";


// ================= MAIN COMPONENT =================
const ThaiRubberChartComponent = () => {
    const theme = useTheme();
    const colors = tokens(theme.palette.mode);
    const { __fetching_data__ } = useFetchingData();

    // ================= [UI STATE] =================
    const [isLoading, setIsLoading] = useState(false);
    const [symbolQuery, setSymbolQuery] = useState("");
    const [showSymbolDropdown, setShowSymbolDropdown] = useState(false);

    const [metrics, setMetrics] = useState([
        { title: "RSS3 (BKK)", value: 78.50, change: 1.2 },
        { title: "TSR20 (SGX)", value: 162.4, change: -0.5 },
        { title: "CUP LUMP", value: 45.20, change: 0.5 },
        { title: "EXCHANGE RATE", value: "...", change: 0.0 },
    ]);

    useEffect(() => {
        const fetchRubberMetrics = async () => {
            try {
                const fxRes = await fetch('https://open.er-api.com/v6/latest/USD');
                const fxData = await fxRes.json();
                const liveUSDTHB = fxData.rates.THB;

                setMetrics(prev => [
                    prev[0],
                    prev[1],
                    prev[2],
                    {
                        title: "USD/THB",
                        value: liveUSDTHB.toFixed(2),
                        change: prev[3].change
                    }
                ]);
            } catch (error) {
                console.error("Error fetching live data:", error);
            }
        };

        fetchRubberMetrics();
        const interval = setInterval(fetchRubberMetrics, 60000);
        return () => clearInterval(interval);
    }, []);

    const [selectedSymbol, setSelectedSymbol] = useState(null);
    const [selectedYear, setSelectedYear] = useState('6M');
    const [paramsQuery, setParamsQuery] = useState({ symbol: "", queryType: 1 });

    const uniqueRandomColors = useRef([]);
    const chartInstanceRefs = useRef([]);
    const [chartContainers, setChartContainers] = useState([]);
    const memoizedChartContainers = useMemo(() => chartContainers, [chartContainers]);

    const charts_option = useMemo(() => [
        {
            crosshair: { mode: 1 },
            localization: { locale: "en-US" },
            layout: {
                attributionLogo: false,
                background: { type: 'solid', color: 'transparent' },
                textColor: theme.palette.text.primary,
            },
            grid: {
                vertLines: { color: theme.palette.divider },
                horzLines: { color: theme.palette.divider },
            },
            rightPriceScale: { borderColor: theme.palette.divider, minimumWidth: 80 },
            timeScale: { borderColor: theme.palette.divider, visible: true },
            priceLineVisible: false,
            lastValueVisible: false,
        },
        {
            crosshair: { mode: 1 },
            localization: { locale: "en-US" },
            layout: {
                attributionLogo: false,
                background: { type: 'solid', color: 'transparent' },
                textColor: theme.palette.text.primary,
            },
            grid: {
                vertLines: { color: theme.palette.divider },
                horzLines: { color: theme.palette.divider },
            },
            rightPriceScale: { borderColor: theme.palette.divider, minimumWidth: 80 },
            timeScale: { borderColor: theme.palette.divider, visible: true },
            priceLineVisible: false,
            lastValueVisible: false
        }
    ], [theme]);

    const deleteExitingline = () => {
        try {
            if (serieInstanceRefs.current.close) {
                Object.values(serieInstanceRefs.current.close).forEach(serie => {
                    if (chartInstanceRefs.current[0] && serie) {
                        chartInstanceRefs.current[0].removeSeries(serie);
                    }
                });
            }
            if (serieInstanceRefs.current.rubber_raw) {
                Object.values(serieInstanceRefs.current.rubber_raw).forEach(serie => {
                    if (chartInstanceRefs.current[1] && serie) {
                        chartInstanceRefs.current[1].removeSeries(serie);
                    }
                });
            }
            serieInstanceRefs.current.close = {};
            serieInstanceRefs.current.rubber_raw = {};
        } catch (error) {
            console.error('Deleting Error', error);
        }
    }

    const serieInstanceRefs = useRef({ close: {}, rubber_raw: {} });
    const [seriesSetting] = useState({
        'mainSeries1': { color: '#7bff71', lineWidth: 3, priceLineVisible: false, lastValueVisible: false },
        'mainSeries2': { color: '#eecb72', lineWidth: 3, priceLineVisible: false },
        'mainSeries3': { color: '#eecb72', lineWidth: 3, priceLineVisible: false },
        'mainSeries5': { color: '#eecb72', lineWidth: 3, priceLineVisible: false },
    });

    const chart_name = ['CLOSE', 'Rubber Thai'];

    // useEffect(() => {
    //     get_data();
    // }, []);

    // useEffect(() => {
    //     setData2Charts();
    // }, [resultClosePrice, resultRubber]);

    useEffect(() => {
        const resizeHandler = () => {
            chartInstanceRefs.current.forEach((chart, index) => {
                if (chart && chartContainers[index]) {
                    try { chart.resize(chartContainers[index].clientWidth, chartContainers[index].clientHeight); } catch (error) { }
                }
            })
        };

        const chartAnnotation = (chart, text) => {
            chart.applyOptions({
                watermark: {
                    visible: true,
                    fontSize: 30,
                    horzAlign: 'center',
                    vertAlign: 'center',
                    color: theme.palette.chart?.watermark || 'rgba(255, 255, 255, 0.1)',
                    text: text,
                },
                rightPriceScale: { visible: true },
            });
        }

        const createChartInstances = () => {
            chartContainers.forEach((container, index) => {
                const chart = createChart(container, charts_option[index ? index : 0]);
                chartAnnotation(chart, chart_name[index]);
                chartInstanceRefs.current[index] = chart;

                const resizeObserver = new ResizeObserver((entries) => {
                    if (entries.length === 0 || entries[0].target !== container) return;
                    try {
                        const newRect = entries[0].contentRect;
                        chart.resize(newRect.width, newRect.height);
                    } catch (e) { }
                });
                resizeObserver.observe(container);
            });
            window.addEventListener('resize', resizeHandler);
        };

        if (chartContainers.length === 2) {
            createChartInstances();
        }

        return () => {
            window.removeEventListener('resize', resizeHandler);
            chartInstanceRefs.current.forEach((chart) => {
                if (chart) {
                    try { chart.remove(); } catch (e) { }
                }
            });
        };
    }, [memoizedChartContainers, charts_option, theme]);

    const handleAddChartContainer = (element) => {
        if (!chartContainers.includes(element) && element) {
            setChartContainers((prevContainers) => [...prevContainers, element]);
        }
    };

    const [resultClosePrice, setResultClosePrice] = useState([]);
    const [resultRubber, setResultRubber] = useState([]);

    const get_data = async (symbolToFetch) => {
        setIsLoading(true);
        try {
            deleteExitingline();
        } catch (error) { }

        let targetSymbol = symbolToFetch || paramsQuery.symbol;
        let values = {
            'symbol': targetSymbol,
            'queryType': targetSymbol ? 2 : 1
        };

        try {
            const response = await __fetching_data__({
                url: `${base_api_url}/rubber_test?${new URLSearchParams(values)}`,
                method: "GET"
            });
            if (response.status && response.data) {
                setResultClosePrice(response.data.close_price || []);
                setResultRubber(response.data.rubber || []);
            }
        } catch (error) {
            console.error(error);
        } finally {
            setTimeout(() => setIsLoading(false), 500);
        }
    };

    const setData2Charts = () => {
        if (!Array.isArray(resultClosePrice) || resultClosePrice.length === 0) return;
        if (!Array.isArray(resultRubber) || resultRubber.length === 0) return;

        const filteredClosePrice = filterByRange(resultClosePrice, selectedYear);
        const filteredRubber = filterByRange(resultRubber, selectedYear);

        let rawData1 = generateData(filteredClosePrice, 'DateTime', 'Close');
        let sortedRubber = [...filteredRubber].sort((a, b) => compareDatetime(a, b, 'Date'));
        let rawData2 = generateData(sortedRubber, 'Date', 'rubber_raw_local');

        const allDatesSet = new Set([...rawData1.map(d => d.time), ...rawData2.map(d => d.time)]);
        const allDatesArray = Array.from(allDatesSet).sort();

        const fillMissingData = (dataArray) => {
            const filledData = [];
            let lastValue = dataArray.length > 0 ? dataArray[0].value : null;
            let dataIndex = 0;

            for (const currentDate of allDatesArray) {
                if (dataIndex < dataArray.length && dataArray[dataIndex].time === currentDate) {
                    lastValue = dataArray[dataIndex].value;
                    filledData.push(dataArray[dataIndex]);
                    dataIndex++;
                } else {
                    if (lastValue !== null) {
                        filledData.push({ time: currentDate, value: lastValue });
                    }
                }
            }
            return filledData;
        };

        const finalData1 = fillMissingData(rawData1);
        const finalData2 = fillMissingData(rawData2);

        if (chartInstanceRefs.current[0] && finalData1.length > 0) {
            let mainSeriesDummy = chartInstanceRefs.current[0].addLineSeries(seriesSetting.mainSeries1);
            mainSeriesDummy.setData(finalData1);
            mainSeriesDummy.applyOptions({
                lastValueVisible: false,
                priceLineVisible: false,
                visible: paramsQuery.queryType == 1 ? false : true
            });
            serieInstanceRefs.current.close['close'] = mainSeriesDummy;
        }

        if (chartInstanceRefs.current[1] && finalData2.length > 0) {
            if (uniqueRandomColors.current.length === 0) {
                uniqueRandomColors.current = generateUniqueRandomColors(1);
            }
            let mainSeriesDummy = chartInstanceRefs.current[1].addLineSeries(seriesSetting.mainSeries5);
            mainSeriesDummy.setData(finalData2);
            mainSeriesDummy.applyOptions({
                color: seriesSetting.mainSeries5.color,
                lastValueVisible: false,
                priceLineVisible: false,
                visible: paramsQuery.queryType == 1 ? false : true
            });
            serieInstanceRefs.current.rubber_raw['rubber-0'] = mainSeriesDummy;
        }

        chartInstanceRefs.current.forEach((currentChart, currentIndex) => {
            currentChart.timeScale().subscribeVisibleLogicalRangeChange(timeRange => {
                chartInstanceRefs.current.forEach((otherChart, otherIndex) => {
                    if (currentIndex !== otherIndex && timeRange) {
                        otherChart.timeScale().setVisibleLogicalRange(timeRange);
                    }
                });
            });
        });

        try {
            const keys_series = Object.keys(serieInstanceRefs.current);
            chartInstanceRefs.current.forEach((currentChart, currentIndex) => {
                currentChart.subscribeCrosshairMove(param => {
                    const firstKey = Object.keys(serieInstanceRefs.current[keys_series[currentIndex]])[0];
                    if (!firstKey) return;
                    const dataPoint = getCrosshairDataPoint(serieInstanceRefs.current[keys_series[currentIndex]][firstKey], param);

                    chartInstanceRefs.current.forEach((otherChart, otherIndex) => {
                        if (currentIndex !== otherIndex) {
                            for (const key_other_serie in serieInstanceRefs.current[keys_series[otherIndex]]) {
                                if (Object.hasOwnProperty.call(serieInstanceRefs.current[keys_series[otherIndex]], key_other_serie)) {
                                    syncCrosshair(otherChart, serieInstanceRefs.current[keys_series[otherIndex]][key_other_serie], dataPoint);
                                }
                            }
                        }
                    });
                });
            });

            if (chartInstanceRefs.current[0]) chartInstanceRefs.current[0].timeScale().fitContent();
            if (chartInstanceRefs.current[1]) chartInstanceRefs.current[1].timeScale().fitContent();
        } catch (error) { }
    }

    useEffect(() => {
        get_data();
    }, []);

    useEffect(() => {
        if (chartContainers.length === 2) {
            deleteExitingline();
            setData2Charts();
        }
    }, [resultClosePrice, resultRubber, chartContainers, selectedYear]);

    function syncCrosshair(chart, series, dataPoint) {
        if (dataPoint) {
            chart.setCrosshairPosition(dataPoint.value, dataPoint.time, series);
            return;
        }
        chart.clearCrosshairPosition();
    }

    function getCrosshairDataPoint(series, param) {
        if (!param.time) return null;
        return param.seriesData.get(series) || null;
    }

    const [symbols, setSymbols] = useState([]);

    const filteredSymbols = symbols.filter((s) =>
        s.toLowerCase().includes(symbolQuery.toLowerCase())
    );

    useEffect(() => {
        const getSymbolList = async () => {
            const response = await __fetching_data__({ url: `${base_api_url}/symbol_list`, method: "GET" });
            if (response.status && Array.isArray(response.data)) {
                const uniqueSymbols = Array.from(new Set(response.data.map(val => val.SymbolName)));
                setSymbols(uniqueSymbols);
            }
        }
        getSymbolList();
    }, []);

    const resChartRef = useRef(null);

    // คำนวณความสูงของแต่ละกราฟสำหรับ Skeleton
    const [skeletonHeight, setSkeletonHeight] = useState(300);
    useEffect(() => {
        const updateHeight = () => {
            if (resChartRef.current) {
                const total = resChartRef.current.clientHeight;
                setSkeletonHeight(Math.max(150, (total - 16) / 2));
            }
        };
        updateHeight();
        window.addEventListener('resize', updateHeight);
        return () => window.removeEventListener('resize', updateHeight);
    }, []);

    return (
        <Box sx={{ width: '100%', minHeight: '100vh', backgroundColor: '#0c111b', paddingTop: '20px' }}>
            <style>
                {`
                @keyframes symbolBounce {
                    0% { transform: scale(1); }
                    30% { transform: scale(1.04); }
                    50% { transform: scale(0.98); }
                    70% { transform: scale(1.02); }
                    100% { transform: scale(1); }
                }
                .symbol-bounce {
                    animation: symbolBounce 1.8s ease-in-out infinite;
                }
                * {
                    scrollbar-width: none;
                    -ms-overflow-style: none;
                }
                *::-webkit-scrollbar {
                    display: none;
                }
                `}
            </style>

            <div className="w-full text-white px-6 py-2">
                <div className="max-w-[1600px] mx-auto">

                    {/* ================= TOP CONTROL BAR ================= */}
                    <div className="flex flex-wrap items-center justify-between gap-4 mb-6">
                        <div className="flex flex-wrap items-center gap-3 z-50">

                            {/* Symbol Input Custom Dropdown */}
                            <div className="relative w-56">
                                <div
                                    className={`
                                        relative bg-[#111827] border border-slate-700 rounded-md px-4 py-3 flex items-center
                                        ${!paramsQuery.symbol && !symbolQuery ? "symbol-bounce" : ""}
                                    `}
                                >
                                    <input
                                        value={symbolQuery}
                                        onChange={(e) => {
                                            setSymbolQuery(e.target.value);
                                            setShowSymbolDropdown(true);
                                        }}
                                        onFocus={() => setShowSymbolDropdown(true)}
                                        placeholder=""
                                        className="w-full bg-transparent outline-none text-white text-sm"
                                    />
                                    <div className="flex items-center gap-2">
                                        {(paramsQuery.symbol || symbolQuery) && (
                                            <button
                                                onClick={() => {
                                                    setSymbolQuery("");
                                                    setParamsQuery(prev => ({ ...prev, symbol: "", queryType: 1 }));
                                                    setSelectedSymbol("");
                                                    get_data("");
                                                }}
                                                className="text-slate-400 hover:text-white text-xs ml-2"
                                            >
                                                ✕
                                            </button>
                                        )}
                                        <span
                                            onClick={() => setShowSymbolDropdown(!showSymbolDropdown)}
                                            className="text-slate-400 text-xs ml-2 cursor-pointer"
                                        >
                                            ▾
                                        </span>
                                    </div>
                                </div>

                                <label
                                    className={`
                                        absolute left-4 px-2 transition-all duration-200 pointer-events-none
                                        ${paramsQuery.symbol || symbolQuery || showSymbolDropdown
                                            ? "-top-2 text-xs bg-[#0c111b]"
                                            : "top-1/2 -translate-y-1/2 text-sm text-slate-400"
                                        }
                                    `}
                                >
                                    Symbol*
                                </label>

                                {showSymbolDropdown && (
                                    <div className="absolute mt-2 w-full bg-[#0f172a] border border-slate-700 rounded-xl shadow-2xl max-h-72 overflow-y-auto z-50">
                                        {filteredSymbols.length > 0 ? (
                                            filteredSymbols.map((item, index) => (
                                                <div
                                                    key={index}
                                                    onClick={() => {
                                                        setSymbolQuery(item);
                                                        setShowSymbolDropdown(false);
                                                        setParamsQuery(prev => ({ ...prev, symbol: item, queryType: 2 }));
                                                        setSelectedSymbol(item);
                                                        get_data(item);
                                                    }}
                                                    className="px-4 py-2 text-sm text-slate-300 hover:bg-cyan-500 hover:text-white cursor-pointer transition"
                                                >
                                                    {item}
                                                </div>
                                            ))
                                        ) : (
                                            <div className="px-4 py-2 text-sm text-slate-500">
                                                No results
                                            </div>
                                        )}
                                    </div>
                                )}
                            </div>
                        </div>

                        {/* Period Buttons */}
                        <div className="flex gap-2">
                            {["3M", "6M", "1Y", "YTD", "ALL"].map((p) => (
                                <button
                                    key={p}
                                    onClick={() => setSelectedYear(p)}
                                    className={`px-3 py-1 text-xs rounded-md border transition-all
                                        ${selectedYear === p
                                            ? "bg-[#1f2937] border-cyan-400 text-cyan-400"
                                            : "border-slate-700 text-slate-400 hover:border-cyan-400 hover:text-cyan-400"
                                        }`}
                                >
                                    {p === "ALL" ? "MAX" : p}
                                </button>
                            ))}
                        </div>
                    </div>

                    {/* ================= METRIC STRIP ================= */}
                    <div style={{ display: 'none' }} className="grid grid-cols-4 gap-3 md:gap-4 mb-6">
                        {metrics.map((m) => (
                            <div
                                key={m.title}
                                className="bg-[#111827] border border-slate-700 px-3 md:px-4 py-3 rounded-xl text-white w-full shadow-md"
                            >
                                <p className="text-[10px] md:text-xs text-slate-400 whitespace-nowrap overflow-hidden text-ellipsis">
                                    {m.title}
                                </p>
                                <div className="flex flex-wrap justify-between items-center mt-2 gap-1">
                                    <p className="text-sm md:text-lg font-bold">{m.value}</p>
                                    <p className={`text-xs font-medium ${m.change > 0 ? "text-green-400" : m.change < 0 ? "text-red-400" : "text-slate-400"}`}>
                                        {m.change > 0 ? "+" : ""}
                                        {m.change}%
                                    </p>
                                </div>
                            </div>
                        ))}
                    </div>

                    {/* ================= CHARTS GRID (with Skeleton overlay while loading) ================= */}
                    <div
                        ref={resChartRef}
                        className="flex flex-col gap-4"
                        style={{
                            height: 'calc(100vh - 200px)',
                            minHeight: '600px',
                        }}
                    >
                        {/* Chart 1 — CLOSE */}
                        <div className="flex-1 relative flex flex-col min-h-0">
                            {/* Skeleton overlay — แสดงทับกราฟขณะโหลด */}
                            {isLoading && (
                                <div className="absolute inset-0 z-10">
                                    <ChartSkeleton title="CLOSE" height={skeletonHeight} />
                                </div>
                            )}
                            {/* Chart container — mount ตลอด ไม่ถูก unmount */}
                            <div
                                className="bg-[#111827] border border-slate-700 rounded-xl p-2 relative flex flex-col w-full h-full min-h-0"
                                style={{ opacity: isLoading ? 0 : 1, transition: 'opacity 0.3s ease' }}
                            >
                                <div
                                    id="chart-close"
                                    className="w-full flex-1"
                                    ref={(element) => handleAddChartContainer(element)}
                                />
                            </div>
                        </div>

                        {/* Chart 2 — Rubber Thai */}
                        <div className="flex-1 relative flex flex-col min-h-0">
                            {/* Skeleton overlay — แสดงทับกราฟขณะโหลด */}
                            {isLoading && (
                                <div className="absolute inset-0 z-10">
                                    <ChartSkeleton title="Rubber Thai" height={skeletonHeight} />
                                </div>
                            )}
                            {/* Chart container — mount ตลอด ไม่ถูก unmount */}
                            <div
                                className="bg-[#111827] border border-slate-700 rounded-xl p-2 relative flex flex-col w-full h-full min-h-0"
                                style={{ opacity: isLoading ? 0 : 1, transition: 'opacity 0.3s ease' }}
                            >
                                <div
                                    id="chart-rubber-raw"
                                    className="w-full flex-1"
                                    ref={(element) => handleAddChartContainer(element)}
                                />
                            </div>
                        </div>
                    </div>

                </div>
            </div>
        </Box>
    )
}
export default ThaiRubberChartComponent;